/**
 * This is taken from
 * https://github.com/hsuanyi-chou/shadcn-ui-expansions
 * https://github.com/hsuanyi-chou/shadcn-ui-expansions/blob/main/components/ui/autosize-textarea.tsx
 *
 * We're using this rather than https://github.com/Andarist/react-textarea-autosize as that
 * library is in maintenance mode and we were hitting issues with it, specifically
 * https://github.com/Andarist/react-textarea-autosize/issues/393
 *
 * Modifications:
 * - Added an enabled prop to disable auto-sizing when not needed
 * - Cleaned up the code a bit
 * - Added debouncing and rAF to reduce layout thrashing in large conversations
 */

import * as React from "react";
import { useDebounce } from "use-debounce";

type UseAutosizeTextAreaProps = {
    textArea: HTMLTextAreaElement | null;
    minHeight?: number;
    maxHeight?: number;
    // The implementation dynamically sets the height of the textarea based on the scrollHeight of the element.
    // The scrollHeight includes the elments padding but not the border[1], so when we're setting the height
    // we need to account for the total height of the border in order to avoid causing the elment to always show
    // scrollbars.
    //
    // A concrete scenario of why the scrollbars would show:
    //  - scrollHeight = 34 (the height of the content + padding)
    //  - borderWidth = 2
    //  - As the height property is for the entire element (including the border) we'd consistenly
    //    setting the height to a value where the content-area + padding can't fit by 2px which causes scrollbars.
    //
    // Note (mads): We should be able to get rid of this prop in favour of reading the borderWidth of the elment
    // but that might have an performance impact, so for now I'm opting to not do it. If we find that the offsetBorder
    // prop gets in the way we can try to get rid of it.
    //
    // [1] https://developer.mozilla.org/en-US/docs/Web/API/Element/scrollHeight
    offsetBorder?: number;
    triggerAutoSize: string;
    enabled?: boolean;
};

export const useAutosizeTextArea = ({
    textArea,
    triggerAutoSize,
    maxHeight = Number.MAX_SAFE_INTEGER,
    minHeight = 0,
    // By default our inputs have a border width of 1px
    offsetBorder = 2,
    enabled = true,
}: UseAutosizeTextAreaProps) => {
    // Use a ref instead of state to avoid an extra render cycle on first init
    const initRef = React.useRef(true);

    // Debounce resize to batch rapid keystrokes and reduce layout thrashing
    const [debouncedTrigger] = useDebounce(triggerAutoSize, 50);

    React.useEffect(() => {
        if (!enabled || !textArea) {
            return;
        }

        // Use rAF to align DOM measurements with the browser's render cycle
        const rafId = requestAnimationFrame(() => {
            // Set min/max height constraints once
            if (initRef.current) {
                textArea.style.minHeight = `${minHeight + offsetBorder}px`;
                if (maxHeight > minHeight) {
                    textArea.style.maxHeight = `${maxHeight}px`;
                }
                initRef.current = false;
            }

            // Reset height to measure natural scrollHeight, then set final height.
            // Both writes + the read happen within a single rAF, so the browser
            // only performs one forced layout recalc for the scrollHeight read.
            textArea.style.height = `${minHeight + offsetBorder}px`;
            const scrollHeight = textArea.scrollHeight;
            textArea.style.height = `${Math.min(scrollHeight + offsetBorder, maxHeight)}px`;
        });

        return () => cancelAnimationFrame(rafId);
    }, [textArea, debouncedTrigger, minHeight, maxHeight, enabled, offsetBorder]);
};
